import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIntegrations, providerReady, isNotConfigured } from "@/context/IntegrationsContext";
import ChatBubble from "@/components/chat/chat_bubble";
import ChatInput from "@/components/chat/chat_input";
import ViewingChatCard from "@/components/viewings/viewing_chat_card";

// Demo-only conversation, shaped like real API messages (body/sender_id/
// created_at). "me" is a placeholder sender that the render below maps to
// the current session user so the demo bubbles sit on the right side.
const FALLBACK = [
  { id: "s1", system: true, body: "You matched. Say hello and ask about a viewing." },
  { id: "a1", sender_id: "other", automated: true, body: "Thanks for the match! I usually reply within a few hours. Tell me a bit about yourself and your ideal move in date.", created_at: "2026-06-30T13:55:00Z" },
  { id: "f1", sender_id: "other", body: "Hi! Thanks for the like. The place is available and I would love to tell you more.", created_at: "2026-06-30T13:58:00Z" },
  { id: "f2", sender_id: "me", body: "It looks perfect. Is the move in date flexible at all?", created_at: "2026-06-30T14:05:00Z" },
  { id: "f3", sender_id: "other", body: "We can work with the first or the fifteenth. Want to book a viewing this week?", created_at: "2026-06-30T14:22:00Z" },
];

// Lister quick actions: one tap drops a prewritten request into the
// composer for editing (never auto sent), then it travels through the
// normal send path like any other message. The screening consent wording
// matches the real renter flow: the "Share my verified profile" switch at
// the top of the renter's chat drives POST /renters/me/screening-consent.
const LISTER_QUICK_ACTIONS = [
  {
    label: "Request references",
    text: "Hi! To help me review your application, could you add one or two references to your SwipRent renter profile? Past landlords or employers work well. Thank you!",
  },
  {
    label: "Request screening consent",
    text: "Hi! Would you be open to sharing your verified profile for screening? Just turn on the Share my verified profile switch at the top of our chat and I can take care of the rest. Thank you!",
  },
  {
    label: "Request proof of income",
    text: "Hi! Could you share a recent proof of income, such as a pay stub or an employment letter? You can bring it to the viewing or send it here. Thank you!",
  },
];

export default function ChatWindow({ matchId, mode, onLoaded }) {
  const [messages, setMessages] = useState([]);
  // The chat dies with the listing: when the match is closed (listing
  // removed or rented to someone else) the composer swaps for a muted
  // closed state banner. The status rides on GET /messages.
  const [matchStatus, setMatchStatus] = useState("active");
  const [offline, setOffline] = useState(false);
  const [bannerH, setBannerH] = useState(0);
  const bottomRef = useRef(null);
  const bannerRef = useRef(null);
  const loadedOnceRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const { user } = useAuth();

  // AI opener suggestions for a brand new thread. Same gating pattern as the
  // renter profile "about me" button: only rendered while OpenAI is
  // configured (providerReady); a 503 from the API additionally hides it
  // quietly with one info toast, in case the status call was stale.
  const { status: integrations } = useIntegrations();
  const [draft, setDraft] = useState("");
  // Lister only: the quick request chips fold behind a small plus button so
  // the composer stays uncluttered until they are wanted.
  const [quickOpen, setQuickOpen] = useState(false);
  const [aiHidden, setAiHidden] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [openers, setOpeners] = useState([]);
  const aiUnavailableToastShown = useRef(false);

  // The demo banner floats over the scroll area, so the message list gets
  // top padding equal to the measured banner height and never hides under it.
  useEffect(() => {
    if (!offline) {
      setBannerH(0);
      return undefined;
    }
    const measure = () => setBannerH(bannerRef.current?.offsetHeight || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [offline]);

  const fetchMessages = useCallback(() => {
    api
      .get(`/matches/${matchId}/messages`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.messages;
        if (res.data?.match_status) setMatchStatus(res.data.match_status);
        if (data) {
          setMessages(data);
          setOffline(false);
          // Fetching marks the thread read server-side; tell the parent once
          // so it can refresh the unread badge immediately.
          if (!loadedOnceRef.current) {
            loadedOnceRef.current = true;
            setLoaded(true);
            onLoaded?.();
          }
        }
      })
      .catch(() => {
        setOffline(true);
        setMessages((m) => (m.length ? m : FALLBACK));
      });
  }, [matchId, onLoaded]);

  useEffect(() => {
    fetchMessages();
    const timer = setInterval(fetchMessages, 4000);
    return () => clearInterval(timer);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  function send(body) {
    const optimistic = { id: `tmp_${Date.now()}`, sender_id: user?.id ?? "me", body, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    api
      .post(`/matches/${matchId}/messages`, { body })
      .then((res) => {
        const saved = res.data?.message;
        const autoReply = res.data?.auto_reply;
        setMessages((m) => {
          let next = saved ? m.map((x) => (x.id === optimistic.id ? saved : x)) : m;
          // The backend may answer immediately on the lister's behalf —
          // show that reply right away instead of waiting for the next poll.
          if (autoReply) next = [...next, autoReply];
          return next;
        });
      })
      .catch((error) => {
        // No response means the backend is offline (demo mode) — keep the
        // local bubble so the demo conversation still flows.
        if (!error.response) return;
        // A real rejection (403 unverified, 422 bad body) must not look
        // like a sent message: drop the optimistic bubble and say why.
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        // The match closed under us (listing removed or rented while the
        // chat was open): flip straight to the closed banner, no toast spam.
        if (error.response.status === 409 && error.response.data?.code === "MATCH_CLOSED") {
          setMatchStatus("closed");
          return;
        }
        toast.error(formatApiError(error));
      });
  }

  async function suggestOpeners() {
    if (aiLoading) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/opener-suggestions`);
      const list = Array.isArray(data?.suggestions) ? data.suggestions.filter(Boolean) : [];
      if (list.length === 0) {
        toast.warning("No suggestions came back. Please try again.");
      }
      setOpeners(list);
    } catch (e) {
      if (isNotConfigured(e)) {
        // OpenAI is not connected: hide the row quietly, tell them once.
        setAiHidden(true);
        if (!aiUnavailableToastShown.current) {
          aiUnavailableToastShown.current = true;
          toast.info("AI suggestions are not available yet.");
        }
      } else if (e?.response?.status === 429) {
        toast.warning("You have used up your suggestions for now. Try again in a little while.");
      } else {
        toast.warning("We could not fetch suggestions right now. Please try again.");
      }
    } finally {
      setAiLoading(false);
    }
  }

  // Demo fallback rows use the "me" placeholder; real rows carry the actual
  // sender_id. Both resolve to the same ownership check here.
  const isMine = (m) => m.sender_id === "me" || (m.sender_id != null && m.sender_id === user?.id);

  // Every viewing transition (propose, counter, approve...) writes its own
  // system message pointing at the SAME viewing, and each card shows the
  // CURRENT state — so two messages meant two identical "Viewing confirmed"
  // cards. Only the newest message per viewing renders the interactive card;
  // the older ones fall through to the plain system pill with their original
  // body, which reads as history instead of a duplicate.
  const lastViewingMessageId = useMemo(() => {
    const last = {};
    for (const m of messages) {
      if (m.system && m.viewing_id) last[m.viewing_id] = m.id;
    }
    return last;
  }, [messages]);

  // The opener row shows ONLY on a fresh thread: real data loaded, no human
  // message yet (system pills like "You matched" do not count), and OpenAI
  // is actually available. The moment any message exists it disappears.
  const hasHumanMessage = messages.some((m) => !m.system);
  const chatClosed = matchStatus !== "active";
  const showOpeners =
    loaded && !offline && !chatClosed && !hasHumanMessage && !aiHidden && providerReady(integrations, "openai");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative min-h-0 flex-1">
        {offline && (
          <p
            ref={bannerRef}
            className="absolute inset-x-0 top-0 z-10 bg-night-tint px-4 py-1.5 text-center text-xs font-medium text-brand-primary"
          >
            Demo conversation while the backend is offline
          </p>
        )}
        <div
          className="h-full space-y-3 overflow-y-auto bg-night p-4"
          style={bannerH ? { paddingTop: bannerH + 16 } : undefined}
        >
          {messages.map((m) =>
            // Viewing system messages carry viewing_id: render the
            // interactive card (Confirm / Reject / Modify) instead of the
            // plain pill. The card re-reads the CURRENT viewing state, so
            // old proposal messages never resurrect dead buttons. Only the
            // NEWEST message per viewing gets the card (older siblings render
            // as plain pills via ChatBubble) so the thread never shows the
            // same viewing card twice.
            m.system && m.viewing_id && lastViewingMessageId[m.viewing_id] === m.id ? (
              <ViewingChatCard key={m.id} viewingId={m.viewing_id} fallbackBody={m.body} />
            ) : (
              <ChatBubble key={m.id} message={m} mine={isMine(m)} />
            )
          )}
          <div ref={bottomRef} />
        </div>
      </div>
      {showOpeners && (
        <div className="border-t border-night-line bg-night-surface px-3 pt-3">
          <button
            type="button"
            onClick={suggestOpeners}
            disabled={aiLoading}
            className="inline-flex items-center gap-1.5 rounded-full border border-night-line bg-night-surface px-3.5 py-1.5 text-xs font-semibold text-night-text transition hover:border-brand-primary hover:bg-night-tint disabled:opacity-50"
          >
            <Sparkles size={14} className="text-brand-primary" />
            {aiLoading ? "Writing suggestions" : "Suggest an opener"}
          </button>
          {openers.length > 0 && (
            <div className="mt-2 grid gap-2">
              {openers.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    setDraft(s);
                    setOpeners([]);
                  }}
                  className="rounded-2xl border border-night-line bg-night-tint/60 px-4 py-2.5 text-left text-sm text-night-text transition hover:border-brand-primary hover:bg-night-tint"
                >
                  {s}
                </button>
              ))}
              <p className="text-xs font-medium text-night-sub">
                Tap a suggestion to use it. You can edit it before sending.
              </p>
            </div>
          )}
        </div>
      )}
      {mode === "lister" && !chatClosed && (
        <div className="border-t border-night-line bg-night-surface px-3 pt-2">
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
            <button
              type="button"
              aria-label={quickOpen ? "Hide quick requests" : "Show quick requests"}
              aria-expanded={quickOpen}
              onClick={() => setQuickOpen((v) => !v)}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
                quickOpen
                  ? "border-brand-primary bg-night-tint text-brand-primary"
                  : "border-night-line bg-night-surface text-night-sub hover:border-brand-primary hover:text-brand-primary"
              }`}
            >
              <Plus size={16} className={`transition-transform ${quickOpen ? "rotate-45" : ""}`} />
            </button>
            {quickOpen &&
              LISTER_QUICK_ACTIONS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => {
                    // Prefill only: the lister reviews and hits send.
                    setDraft(a.text);
                    setQuickOpen(false);
                  }}
                  className="shrink-0 rounded-full border border-night-line bg-night-surface px-3.5 py-1.5 text-xs font-semibold text-night-text transition hover:border-brand-primary hover:bg-night-tint"
                >
                  {a.label}
                </button>
              ))}
          </div>
        </div>
      )}
      {chatClosed ? (
        // Muted closed state instead of the composer: the history above
        // stays readable, but no new messages can start.
        <div className="border-t border-night-line bg-night-surface px-4 py-4 text-center">
          <p className="text-sm font-semibold text-night-sub">
            This conversation has ended because the listing is no longer available.
          </p>
        </div>
      ) : (
        <ChatInput onSend={send} value={draft} onChange={setDraft} />
      )}
    </div>
  );
}
